import React, { useState } from "react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import PageSeo from "../SEO/PageSeo"; // ── ADDED SEO IMPORT

const NAVY = "#0d1f3c";

// Adjust to wherever your API is served from (e.g. an env var in a real build).
const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const PARTNER_INQUIRY_ENDPOINT = `${API_BASE_URL}/api/partner-inquiries/`;

const DARK = {
  bg: "#0d1f3c", surface: "#142a4d", surface2: "#1a3358",
  border: "rgba(255,255,255,0.10)",
  accent: "#3b82f6", accentL: "#60a5fa", accentBg: "rgba(59,130,246,0.12)",
  green: "#34d399", greenBg: "rgba(52,211,153,0.10)",
  text: "#f0f4f8", textMid: "rgba(240,244,248,0.78)", textSoft: "rgba(240,244,248,0.50)",
};

const LIGHT = {
  bg: "#f8fafc", surface: "#ffffff", surface2: "#f1f5f9",
  border: "#e2e8f0",
  accent: "#2563eb", accentL: "#3b82f6", accentBg: "rgba(37,99,235,0.08)",
  green: "#16a34a", greenBg: "rgba(22,163,74,0.08)",
  text: "#0f172a", textMid: "#475569", textSoft: "#94a3b8",
};

const BENEFITS = [
  { icon: "📣", title: "Priority Visibility",     desc: "Your loads and jobs surface higher for verified drivers actively searching in your lanes." },
  { icon: "🛡️", title: "Verified Network Access", desc: "Book only ID-checked, certification-verified escort drivers — no cold-calling Facebook groups." },
  { icon: "🤝", title: "Co-Marketing",            desc: "Joint case studies, logo placement on our site, and cross-promotion to our driver and company base." },
  { icon: "💳", title: "Preferred Rates",         desc: "Lower platform fees on invoicing and job posting once you hit partner tier." },
  { icon: "🎧", title: "Dedicated Support",       desc: "A named account contact instead of a support queue — for onboarding, disputes, or scaling up." },
  { icon: "📊", title: "Reporting & Insights",    desc: "Lane-level demand data and driver-availability trends to help you plan ahead." },
];

const STEPS = [
  { n: "1", title: "Apply",   desc: "Tell us about your company — fleet size, lanes you run, and what you're looking for." },
  { n: "2", title: "Verify",  desc: "We confirm your business details, insurance, and operating history — usually within 2 business days." },
  { n: "3", title: "Go Live", desc: "Your logo goes up, your account manager reaches out, and you start booking from our verified network." },
];

const PARTNERSHIP_TYPES = [
  "Carrier / Trucking Company",
  "Insurance Provider",
  "Equipment Supplier",
  "Vehicle Vendor",
  "Software & Technology",
  "Marketing & Promotion",
  "Other",
];

export default function BecomePartnerPage({ isDarkMode, toggleTheme }) {
  const T = isDarkMode ? DARK : LIGHT;
  const [form, setForm] = useState({ company: "", contact: "", email: "", phone: "", website: "", partnershipType: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoError, setLogoError] = useState("");

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setLogoError("Please upload an image file (PNG, JPG, or SVG).");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setLogoError("Please keep the logo under 3MB.");
      return;
    }
    setLogoError("");
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company.trim() || !form.contact.trim() || !form.email.trim() || !form.partnershipType) {
      setError("Company name, your name, email, and partner type are required.");
      return;
    }
    setError("");
    setSubmitting(true);

    try {
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      if (logoFile) body.append("logo", logoFile);

      const res = await fetch(PARTNER_INQUIRY_ENDPOINT, { method: "POST", body });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        const firstError = data.errors && Object.values(data.errors)[0];
        setError(firstError || "Something went wrong submitting your inquiry. Please try again.");
        return;
      }

      setSubmitted(true);
    } catch (err) {
      setError("We couldn't reach the server. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    padding: "12px 14px", borderRadius: 10,
    border: `1px solid ${T.border}`, background: T.surface2,
    color: T.text, fontSize: 14, fontFamily: "inherit", outline: "none",
  };
  const labelStyle = { fontSize: 12.5, fontWeight: 700, color: T.textMid, marginBottom: 6, display: "block" };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Inter',sans-serif" }}>
      
      {/* ── SEO INJECTION ── */}
      <PageSeo 
        title="Partner With PilotLink USA | Carrier & Logistics Partnerships"
        description="Join the PilotLink USA partner program. Get priority access to our verified pilot car driver network, co-marketing support, and a dedicated account manager."
        canonicalUrl="/become-partner"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Partner With PilotLink USA",
          "description": "Carrier and logistics partnership application for PilotLink USA."
        }}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@700;800;900&display=swap');
        *{box-sizing:border-box;}
        .pp-input:focus { border-color: ${T.accent} !important; }
        .pp-select { appearance: none; -webkit-appearance:none; -moz-appearance:none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6'><path d='M0 0l5 6 5-6z' fill='%2394a3b8'/></svg>");
          background-repeat:no-repeat; background-position: right 14px center; padding-right: 34px;
        }
      `}</style>

      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

      {/* Hero */}
      <section style={{
        background: `linear-gradient(160deg, ${NAVY} 0%, #1a3358 60%, ${NAVY} 100%)`,
        padding: "clamp(48px, 8vw, 84px) clamp(20px, 4vw, 40px)", textAlign: "center",
      }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(59,130,246,0.14)", border: "1px solid rgba(96,165,250,0.35)",
            borderRadius: 99, padding: "5px 14px", marginBottom: 18,
            fontSize: 12, fontWeight: 700, color: "#93c5fd",
          }}>
            Carrier & Logistics Partnerships
          </div>
          <h1 style={{
            fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(30px, 5vw, 46px)",
            fontWeight: 900, color: "#fff", lineHeight: 1.15, marginBottom: 14,
          }}>
            Partner With PilotLink USA
          </h1>
          <p style={{ color: "rgba(240,244,248,0.72)", fontSize: "clamp(15px, 2vw, 17px)", lineHeight: 1.7 }}>
            Get priority access to our verified escort driver network, co-marketing support, and a dedicated account manager for your team.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section style={{ padding: "clamp(44px, 7vw, 72px) clamp(20px, 4vw, 40px)" }}>
        <div style={{ maxWidth: 1140, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "clamp(28px, 5vw, 40px)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: isDarkMode ? T.accentL : T.accent, marginBottom: 8 }}>
              Why Partner
            </div>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(24px, 4vw, 32px)", fontWeight: 800, color: T.text }}>
              Built for Carriers Who Move Volume
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "clamp(14px, 2.5vw, 20px)" }}>
            {BENEFITS.map((b) => (
              <div key={b.title} style={{
                background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
                padding: "clamp(18px, 2.5vw, 24px)",
              }}>
                <div style={{
                  width: 46, height: 46, borderRadius: 12, background: T.accentBg,
                  border: `1px solid ${T.accent}33`, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 22, marginBottom: 14,
                }}>{b.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 6 }}>{b.title}</div>
                <div style={{ fontSize: 13.5, color: T.textMid, lineHeight: 1.6 }}>{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ background: isDarkMode ? T.surface : T.surface2, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, padding: "clamp(44px, 7vw, 72px) clamp(20px, 4vw, 40px)" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px, 3.5vw, 28px)", fontWeight: 800, color: T.text, textAlign: "center", marginBottom: "clamp(28px, 5vw, 40px)" }}>
            How to Become a Partner
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: "clamp(16px, 3vw, 24px)" }}>
            {STEPS.map((s) => (
              <div key={s.n} style={{ textAlign: "center" }}>
                <div style={{
                  width: 44, height: 44, borderRadius: "50%", background: T.accent, color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 800, fontSize: 17, margin: "0 auto 14px",
                }}>{s.n}</div>
                <div style={{ fontWeight: 700, fontSize: 15.5, color: T.text, marginBottom: 6 }}>{s.title}</div>
                <div style={{ fontSize: 13.5, color: T.textMid, lineHeight: 1.6, maxWidth: 260, margin: "0 auto" }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / application form */}
      <section style={{ padding: "clamp(44px, 7vw, 72px) clamp(20px, 4vw, 40px)" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "clamp(24px, 4vw, 32px)" }}>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px, 3.5vw, 28px)", fontWeight: 800, color: T.text, marginBottom: 8 }}>
              Tell Us About Your Company
            </h2>
            <p style={{ fontSize: 14, color: T.textSoft }}>
              A few quick questions — our partnerships team typically responds within one business day.
            </p>
          </div>

          {submitted ? (
            <div style={{
              background: T.greenBg, border: `1px solid ${T.green}44`, borderRadius: 14,
              padding: "clamp(28px, 4vw, 36px)", textAlign: "center",
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%", background: T.green, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 26, fontWeight: 800, margin: "0 auto 16px",
              }}>✓</div>
              <div style={{ fontWeight: 800, fontSize: 19, color: T.text, marginBottom: 8, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                Thank you for your interest in partnering with us
              </div>
              <div style={{ fontSize: 14, color: T.textMid, lineHeight: 1.7, maxWidth: 420, margin: "0 auto" }}>
                We've received your application on behalf of <strong style={{ color: T.text }}>{form.company || "your company"}</strong>. A confirmation has been sent to <strong style={{ color: T.text }}>{form.email}</strong>, and our partnerships team will follow up within 24 hours to discuss next steps.
              </div>
              <div style={{
                marginTop: 20, paddingTop: 20, borderTop: `1px solid ${T.green}33`,
                fontSize: 12.5, color: T.textSoft,
              }}>
                Questions in the meantime? Reach us directly at <strong style={{ color: T.textMid }}>partners@pilotlinkusa.org</strong>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{
              background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16,
              padding: "clamp(20px, 3vw, 30px)", display: "flex", flexDirection: "column", gap: 16,
            }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Company Name *</label>
                  <input className="pp-input" style={inputStyle} value={form.company} onChange={update("company")} placeholder="e.g. Acme Freight Co." />
                </div>
                <div>
                  <label style={labelStyle}>Your Full Name *</label>
                  <input className="pp-input" style={inputStyle} value={form.contact} onChange={update("contact")} placeholder="e.g. Jane Smith" />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Work Email *</label>
                  <input className="pp-input" type="email" style={inputStyle} value={form.email} onChange={update("email")} placeholder="e.g. jane@acmefreight.com" />
                </div>
                <div>
                  <label style={labelStyle}>Phone Number</label>
                  <input className="pp-input" style={inputStyle} value={form.phone} onChange={update("phone")} placeholder="e.g. (555) 123-4567" />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 16 }}>
                <div>
                  <label style={labelStyle}>Company Website</label>
                  <input className="pp-input" style={inputStyle} value={form.website} onChange={update("website")} placeholder="https://" />
                </div>
                <div>
                  <label style={labelStyle}>What Best Describes You? *</label>
                  <select
                    className="pp-input pp-select"
                    style={inputStyle}
                    value={form.partnershipType}
                    onChange={update("partnershipType")}
                  >
                    <option value="">Select an option</option>
                    {PARTNERSHIP_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Tell Us About Your Business</label>
                <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 8 }}>
                  Fleet size, lanes you run, or how you'd like to work with us — whatever's useful for our team to know.
                </div>
                <textarea className="pp-input" style={{ ...inputStyle, minHeight: 100, resize: "vertical" }} value={form.message} onChange={update("message")} placeholder="" />
              </div>

              {/* Logo upload — instant preview (Sample Logos Removed) */}
              <div>
                <label style={labelStyle}>Your Company Logo (optional)</label>
                <div style={{ fontSize: 12.5, color: T.textSoft, marginBottom: 8, lineHeight: 1.5 }}>
                  Add your logo and we'll show you exactly how it will appear alongside our other partners — no need to wait for a mock-up.
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ fontSize: 13, color: T.textMid }}
                />
                {logoError && <div style={{ fontSize: 12.5, fontWeight: 600, color: "#dc2626", marginTop: 6 }}>{logoError}</div>}

                {logoPreview && (
                  <div style={{
                    marginTop: 14, background: isDarkMode ? T.surface2 : "#eff6ff",
                    border: `1px dashed ${T.accent}66`, borderRadius: 12,
                    padding: "clamp(14px, 2.5vw, 18px)",
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: isDarkMode ? T.accentL : T.accent, marginBottom: 10 }}>
                      Preview — how you'll appear on our site
                    </div>
                    
                    {/* Only the user's uploaded logo is shown here now */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))", gap: 10 }}>
                      <div style={{
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
                        background: T.surface, border: `2px solid ${T.accent}`, borderRadius: 12,
                        padding: "12px 10px", textAlign: "center",
                      }}>
                        <img src={logoPreview} alt="Your logo preview" style={{ maxHeight: 26, maxWidth: 100, objectFit: "contain" }} />
                        <span style={{ fontSize: 10, fontWeight: 800, color: isDarkMode ? T.accentL : T.accent, letterSpacing: ".03em" }}>{form.company || "YOUR COMPANY"}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div style={{ fontSize: 13, fontWeight: 600, color: "#dc2626" }}>{error}</div>
              )}

              <button type="submit" disabled={submitting} style={{
                background: T.accent, color: "#fff", border: "none", borderRadius: 10,
                padding: "14px", fontWeight: 700, fontSize: 15, cursor: submitting ? "default" : "pointer",
                fontFamily: "inherit", opacity: submitting ? 0.7 : 1,
                boxShadow: `0 4px 14px ${T.accentBg}`,
              }}>
                {submitting ? "Submitting…" : "Submit Partner Application →"}
              </button>
              <div style={{ fontSize: 12, color: T.textSoft, textAlign: "center" }}>
                By submitting, you agree to be contacted by our partnerships team. Prefer email? Reach us directly at <strong style={{ color: T.textMid }}>partners@pilotlinkusa.org</strong>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer isDarkMode={isDarkMode} />
    </div>
  );
}